import { AppConfigService } from './app_config_service.js';
import { VersionLoader } from '../version_loader.js';
import { DialogManager } from '../ui/dialog/dialog_manager.js';
import {
  AppVersionStatus,
  IVersionCheckResult,
  UpdateRequiredDialog,
  UpdateRequiredMode
} from '../ui/dialog/update_required_dialog.js';
import { NetworkManager } from '../network_manager.js';
import { ScreenManager, Screens } from '../screen_manager/screen_manager.js';

export function checkAppVersionStatus(): IVersionCheckResult
{
  const platform = ( window as any ).Capacitor?.getPlatform?.() || 'web';
  if ( platform !== 'android' )
    return { status: AppVersionStatus.Ok, soft_version: 0 };

  const cfg = AppConfigService.get().app_version.android;
  const build_num = VersionLoader.getBuildNumber();

  if ( cfg.hard > 0 && build_num < cfg.hard )
    return { status: AppVersionStatus.Hard, soft_version: 0 };

  if ( cfg.soft > 0 && build_num < cfg.soft )
  {
    if ( UpdateRequiredDialog.isSoftDismissed( cfg.soft ) )
      return { status: AppVersionStatus.Ok, soft_version: cfg.soft };

    return { status: AppVersionStatus.Soft, soft_version: cfg.soft };
  }

  return { status: AppVersionStatus.Ok, soft_version: 0 };
}

let shown_soft_version = 0;
let pending_open: ReturnType<typeof setTimeout> | null = null;
let hooks_installed = false;

function isBattleActive(): boolean
{
  return ScreenManager.getCurrentScreen() === Screens.GAME;
}

export function isHardUpdatePending(): boolean
{
  return checkAppVersionStatus().status === AppVersionStatus.Hard && !UpdateRequiredDialog.isHardOpen();
}

function ensureHooks(): void
{
  if ( hooks_installed ) return;
  hooks_installed = true;

  UpdateRequiredDialog.on_hard_closed = () =>
  {
    setTimeout( () => maybeShowUpdateDialog( { allow_soft: false } ), 300 );
  };

  ScreenManager.addOnChangeListener( next =>
  {
    if ( next !== Screens.GAME )
      maybeShowUpdateDialog( { allow_soft: false } );
  } );
}

export interface IShowUpdateDialogOptions
{
  allow_soft?: boolean;
}

export function maybeShowUpdateDialog( options: IShowUpdateDialogOptions = {} ): void
{
  ensureHooks();

  const allow_soft = options.allow_soft ?? true;
  const { status, soft_version } = checkAppVersionStatus();

  if ( status === AppVersionStatus.Ok )
    return;

  if ( isBattleActive() )
    return;

  if ( status === AppVersionStatus.Hard && UpdateRequiredDialog.isHardOpen() )
    return;

  if ( status === AppVersionStatus.Soft )
  {
    if ( !allow_soft || shown_soft_version >= soft_version )
      return;
    shown_soft_version = soft_version;
  }

  const mode = status === AppVersionStatus.Hard
    ? UpdateRequiredMode.Hard
    : UpdateRequiredMode.Soft;

  if ( pending_open )
  {
    clearTimeout( pending_open );
    pending_open = null;
  }

  const open = () =>
  {
    pending_open = null;

    if ( !DialogManager.ctx )
    {
      pending_open = setTimeout( open, 500 );
      return;
    }

    if ( isBattleActive() )
      return;

    if ( mode === UpdateRequiredMode.Hard )
    {
      if ( UpdateRequiredDialog.isHardOpen() )
        return;
      NetworkManager.setHardUpdateActive();
    }

    const dialog = new UpdateRequiredDialog( mode, soft_version );
    if ( mode === UpdateRequiredMode.Hard && DialogManager.isAnyDialogOpened() )
      DialogManager.routeToDialog( dialog );
    else
      DialogManager.openDialog( dialog );
  };

  open();
}
